import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';

export interface TangramSelectOption {
  value: string;
  label: string;
}

@Component({
  selector: 'tangram-select',
  templateUrl: './tangram-select.component.html',
  styleUrls: ['./tangram-select.component.scss'],
})
export class TangramSelectComponent {
  @Input() options: TangramSelectOption[] = [];
  @Input() selected = '';
  @Input() placeholder = 'Select…';
  @Input() disabled = false;

  @Output() selectedChange = new EventEmitter<string>();

  isOpen = false;
  highlightIndex = -1;

  @ViewChild('optionsList', { static: false }) optionsList?: ElementRef<HTMLUListElement>;

  constructor(private host: ElementRef<HTMLElement>) {}

  get triggerLabel(): string {
    const match = this.options.find((o) => o.value === this.selected);
    return match ? match.label : this.placeholder;
  }

  open(): void {
    if (this.disabled || this.isOpen) { return; }
    this.isOpen = true;
    this.highlightIndex = this.indexOfSelected();
    setTimeout(() => this.scrollHighlightIntoView(), 0);
  }

  close(): void {
    this.isOpen = false;
  }

  toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  selectOption(option: TangramSelectOption): void {
    this.close();
    if (option.value !== this.selected) {
      this.selectedChange.emit(option.value);
    }
  }

  isSelected(option: TangramSelectOption): boolean {
    return option.value === this.selected;
  }

  trackByValue(_idx: number, option: TangramSelectOption): string {
    return option.value;
  }

  onKeydown(event: KeyboardEvent): void {
    if (!this.isOpen) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        this.open();
      }
      return;
    }

    const list = this.options;
    if (!list.length) {
      if (event.key === 'Escape') { event.preventDefault(); this.close(); }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.highlightIndex = (this.highlightIndex + 1) % list.length;
      this.scrollHighlightIntoView();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.highlightIndex = (this.highlightIndex - 1 + list.length) % list.length;
      this.scrollHighlightIntoView();
    } else if (event.key === 'Home') {
      event.preventDefault();
      this.highlightIndex = 0;
      this.scrollHighlightIntoView();
    } else if (event.key === 'End') {
      event.preventDefault();
      this.highlightIndex = list.length - 1;
      this.scrollHighlightIntoView();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const target = list[this.highlightIndex];
      if (target) { this.selectOption(target); }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) { return; }
    const target = event.target as Node | null;
    if (target && this.host.nativeElement.contains(target)) { return; }
    this.close();
  }

  private indexOfSelected(): number {
    for (let i = 0; i < this.options.length; i += 1) {
      if (this.isSelected(this.options[i])) { return i; }
    }
    return 0;
  }

  private scrollHighlightIntoView(): void {
    if (!this.optionsList || this.highlightIndex < 0) { return; }
    const ul = this.optionsList.nativeElement;
    const target = ul.children[this.highlightIndex] as HTMLElement | undefined;
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ block: 'nearest' });
    }
  }
}
